import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokApi } from "@/lib/storyblok-api";

import type { RichTextValue } from "@/components/rich-text";

import { storyblokCache } from "./storyblok-cache";
import type { StoryblokLink } from "./storyblok-link";

type StoryblokAsset = { filename?: string; alt?: string; focus?: string };

type ClinicServiceBlok = { _uid?: string; name?: string; link?: StoryblokLink };

export type ClinicContent = {
  title?: string;
  region?: string;
  country?: string;
  flagship?: boolean;
  tagline?: string;
  address_line1?: string;
  address_line2?: string;
  phone?: string;
  email?: string;
  latitude?: number | string;
  longitude?: number | string;
  photo?: StoryblokAsset;
  services?: ClinicServiceBlok[];
  services_image?: StoryblokAsset;
  summary?: RichTextValue;
  button_label?: string;
  button_link?: StoryblokLink;
  about?: RichTextValue;
  stats?: Array<{ number?: string; label?: string }>;
  opening_hours?: string;
  location_note?: string;
  about_image?: StoryblokAsset;
  parking_car?: string;
  parking_transport?: string;
  parking_accessibility?: string;
  body?: SbBlokData[];
};

export type ClinicStory = {
  uuid: string;
  name: string;
  slug: string;
  full_slug: string;
  content: ClinicContent;
};

export type Clinic = {
  uuid: string;
  fullSlug: string;
  name: string;
  region: string;
  country: string;
  flagship: boolean;
  tagline: string;
  addressLine1: string;
  addressLine2: string;
  phone: string;
  email: string;
  lat: number | null;
  lng: number | null;
  photo: { filename: string; alt: string; focus?: string } | null;
  summary: RichTextValue;
};

export type ServiceLink = { name: string; href: string | null; target?: string };

function num(value: unknown): number | null {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(n) ? n : null;
}

export function toClinic(story: ClinicStory): Clinic {
  const c = story.content || {};
  return {
    uuid: story.uuid,
    fullSlug: story.full_slug,
    name: story.name,
    region: c.region || "",
    country: c.country || "Ireland",
    flagship: Boolean(c.flagship),
    tagline: c.tagline || "",
    addressLine1: c.address_line1 || "",
    addressLine2: c.address_line2 || "",
    phone: c.phone || "",
    email: c.email || "",
    lat: num(c.latitude),
    lng: num(c.longitude),
    photo: c.photo?.filename
      ? { filename: c.photo.filename, alt: c.photo.alt || "", focus: c.photo.focus }
      : null,
    summary: c.summary ?? "",
  };
}

const SAME_CLINIC: Record<string, string> = { Londonderry: "Derry" };
const UK_NAME: Record<string, string> = { Derry: "Londonderry" };

export type ClinicChoice = { label: string; value: string };

const BOTH_NAMES = "Derry / Londonderry";

export function byName(clinics: Clinic[]): Clinic[] {
  return [...clinics].sort((a, b) => a.name.localeCompare(b.name, "en"));
}

export function mergeRegionalPair(clinics: Clinic[]): Clinic[] {
  const out: Clinic[] = [];
  let merged = false;
  for (const clinic of clinics) {
    if (clinic.name !== "Derry" && clinic.name !== "Londonderry") {
      out.push(clinic);
      continue;
    }
    if (merged) continue;
    merged = true;
    out.push({ ...clinic, name: BOTH_NAMES, region: BOTH_NAMES });
  }
  return out;
}

export function clinicChoices(clinics: Clinic[], region: string): ClinicChoice[] {
  const seen = new Set<string>();
  const out: ClinicChoice[] = [];
  for (const clinic of byName(clinics)) {
    const value = SAME_CLINIC[clinic.name] ?? clinic.name;
    if (seen.has(value)) continue;
    seen.add(value);
    out.push({ value, label: region === "uk" ? (UK_NAME[value] ?? value) : value });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, "en"));
}

export function clinicUrl(clinic: Clinic): string {
  const path = clinic.fullSlug.replace(/^(ie|uk)\//, "");
  const prefix = clinic.fullSlug.startsWith("uk/") ? "/uk" : "";
  return `${prefix}/${path}`;
}

function isUkCountry(country: string | undefined): boolean {
  return (country || "").toLowerCase().includes("kingdom");
}

export async function fetchClinics(isDraft = false): Promise<Clinic[]> {
  const api = await storyblokApi();
  const all: ClinicStory[] = [];
  try {
    for (const prefix of ["ie/clinics/", "uk/clinics/"]) {
      let page = 1;
      while (true) {
        const { data } = await api.get(
          "cdn/stories",
          {
            starts_with: prefix,
            filter_query: { component: { in: "clinic" } },
            sort_by: "position:asc",
            per_page: 100,
            page,
            version: isDraft ? "draft" : "published",
          },
          storyblokCache(isDraft, ["clinics"]),
        );
        const stories = (data?.stories || []) as ClinicStory[];
        all.push(...stories);
        if (stories.length < 100) break;
        page++;
        if (page > 5) break;
      }
    }
  } catch {
    return [];
  }
  const byKey = new Map<string, ClinicStory>();
  for (const story of all) {
    const key = story.full_slug.replace(/^(ie|uk)\//, "");
    const folderMatchesCountry =
      story.full_slug.startsWith("uk/") === isUkCountry(story.content?.country);
    if (!byKey.has(key) || folderMatchesCountry) byKey.set(key, story);
  }
  return [...byKey.values()].map(toClinic);
}
